/* istanbul ignore file */
import React from 'react'
import classnames from 'classnames/bind'
import type { Node } from '../../../types'
import Text from '../text'
import Icon from '../icon'
import JsonViewer from '../jsonViewer'
import JsonEditor from '../jsonEditor'
import type { JsonEditorSchema } from '../jsonEditor/schema'
import { AutomationTarget } from '../../../types'
import useBreakpoints from '../../../hooks/useBreakpoints'
import CardContainer from '../cardContainer'
import { ColorNames } from '../../../constants/colorVars'
import styles from './styles.scss'

const cx = classnames.bind(styles)

interface JsonBeforeAfterProps extends AutomationTarget {
  before: Node
  after: Node
  beforeTitle?: string
  afterTitle?: string
  showAfterOnly?: boolean
  afterOnlyTitle?: string
  className?: string
  /**
   * When `true` and `afterSchema` + `onAfterChange` are provided,
   * the "after" (or "afterOnly") panel renders as an editable `JsonEditor`.
   */
  afterEditable?: boolean
  /** Schema describing the structure of the `after` data for editing. */
  afterSchema?: JsonEditorSchema
  /** Called with the full updated `after` data whenever an edit is made. */
  onAfterChange?: (updatedData: Node) => void
}

const JsonBeforeAfter: React.FC<JsonBeforeAfterProps> = ({
  before,
  after,
  beforeTitle = 'Current',
  afterTitle = 'Proposed Changes',
  showAfterOnly = false,
  afterOnlyTitle = 'Changes',
  className,
  afterEditable = false,
  afterSchema,
  onAfterChange,
}) => {
  const breakpoint = useBreakpoints()

  const useEditor = afterEditable && !!afterSchema && !!onAfterChange

  const AfterPanel = useEditor ? (
    <JsonEditor data={after} schema={afterSchema} onChange={onAfterChange} />
  ) : (
    <JsonViewer data={after} />
  )

  return showAfterOnly ? (
    <div className={cx('one-column-layout', className)}>
      <div className={cx('column')}>
        <CardContainer
          padding='small'
          borderStyle='gradient'
          borderColor={ColorNames.Success}
        >
          <Text weight='heavy'>{afterOnlyTitle}</Text>
          {AfterPanel}
        </CardContainer>
      </div>
    </div>
  ) : (
    <div className={cx('three-column-layout', className)}>
      <div className={cx('column')}>
        <CardContainer
          padding='small'
          borderStyle='gradient'
          borderColor={ColorNames.Info}
        >
          <Text weight='heavy'>{beforeTitle}</Text>
          <JsonViewer data={before} />
        </CardContainer>
      </div>
      <div className={cx('column', 'icon-column')}>
        <Icon
          name={
            breakpoint === 'mobile' || breakpoint === 'tablet'
              ? 'circle-arrow-down'
              : 'circle-arrow-right'
          }
          color={ColorNames.GrayMedium}
          iconStyle='light'
          size='huge'
        />
      </div>
      <div className={cx('column')}>
        <CardContainer
          padding='small'
          borderStyle='gradient'
          borderColor={ColorNames.Success}
        >
          <Text weight='heavy'>{afterTitle}</Text>
          {AfterPanel}
        </CardContainer>
      </div>
    </div>
  )
}

export default JsonBeforeAfter
